// @ts-nocheck

import type { AxiosRequestConfig } from "axios";
import _ from "lodash";
import { defineStore } from "pinia";
import type { Ref } from "vue";
import {
    computed,
    nextTick,
    reactive,
    ref,
    type UnwrapNestedRefs,
    watch,
} from "vue";
import { toast } from "vue-sonner";
import {boolean, date, number, object, string} from "yup";
import type { ContactDetailsResponse } from "../../responses/Site/ContactDetailsResponse.ts";
import type { EnrollmentDraftResponse } from "../../responses/Site/EnrollmentDraftResponse.ts";
import type { InvoiceDetailsResponse } from "../../responses/Site/InvoiceDetailsResponse.ts";
import type { StudentResponse } from "../../responses/Site/StudentResponse.ts";
import { label } from "../mixins/translations.ts";
import { api } from "../scripts/api.ts";

export const useEnrollmentStore = defineStore("enrollment", () => {
    const isLoading: Ref<boolean> = ref(true);

    const currentStep: Ref<number> = ref(1);

    const enrollment: Ref<EnrollmentDraftResponse | null> = ref(null);
    const uuid: Ref<string | null> = ref(null);

    const students: Ref<Array<StudentResponse>> = ref([]);
    const contactDetails: Ref<ContactDetailsResponse> = ref({});
    const invoiceDetails: Ref<InvoiceDetailsResponse> = ref({});

    const openStep1: Ref<boolean> = ref(false);
    const openStep2: Ref<boolean> = ref(false);
    const openStep3: Ref<boolean> = ref(false);

    const saveStepRequest: UnwrapNestedRefs<{
        timeout: number | null;
        abort: AbortController | null;
    }> = reactive({
        timeout: null,
        abort: null,
    });

    const emailValidation = string()
        .required(label("site.errors.required"))
        .matches(
            /^[^\s@.][^\s@]*(?<!\.)@[^\s@]+\.[^\s@]+$/,
            label("site.enrollment.email.error"),
        )
        .test(
            "no-double-dots",
            label("site.enrollment.email.error"),
            (value) => !value || !value.split("@")[0].includes(".."),
        )
        .test(
            "no-illegal-chars",
            label("site.enrollment.email.error"),
            (value) => !value || !/[!#$?%^&*()=[\]{}|;:'"<>,/]/.test(value),
        ).test(
            "no-non-ascii",
            label("site.enrollment.email.error"),
            (value) => !value || /^[\x00-\x7F]+$/.test(value),
        );

    const dateOfBirthValidation = date()
        .transform((value, originalValue) => {
            if (typeof originalValue === "string") {
                // HTML <input type="date"> emits yyyy-MM-dd
                const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(originalValue);
                if (!match) return new Date("invalid");
                const [, yyyy, mm, dd] = match;
                const year = Number(yyyy);
                // Reject years before 1900 — catches 0088, 0075, etc.
                if (year < 1900) return new Date("invalid");
                const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
                return isNaN(d.getTime()) ? new Date("invalid") : d;
            }
            return value;
        })
        .typeError(label("site.enrollment.date_of_birth.error"))
        .required(label("site.enrollment.date_of_birth.error"))
        .max(new Date(), label("site.enrollment.date_of_birth.error"))
        .min(
            (() => {
                const maxAge = new Date();
                maxAge.setFullYear(maxAge.getFullYear() - 70);
                return maxAge;
            })(),
            label("site.enrollment.date_of_birth.max_age.error"),
        )
        .test(
            "min-age",
            label("site.enrollment.date_of_birth.min_age.error"),
            (value) => {
                if (!value) return true;
                const minAge = new Date();
                minAge.setFullYear(minAge.getFullYear() - 16);
                return value <= minAge;
            },
        );

    const phoneValidation = string()
        .test(
            "phone-valid",
            label("site.enrollment.phone_number.error"),
            (value) => {
                if (!value) return true; //for optional/nullable, catched by required

                // Stap 1: basisformaat valideren
                const phoneRegex =
                    /^(\(\+\d{1,3}\)|\+\d{1,3}|00\d{1,3}|0)[-.\s]?\d+([-.\s]\d+)*$/;
                if (!phoneRegex.test(value)) {
                    return false;
                }

                // Stap 2: alleen cijfers tellen – minimaal 10 (voor NL), maximaal 15 (E.164 standaard (eigenlijk 8))
                const digitsOnly = value.replace(/\D/g, "");
                if (digitsOnly.length < 10 || digitsOnly.length > 15) {
                    return false;
                }

                // Stap 3: nepadressen uitsluiten // vanaf 7 nullen op het einde error
                if (/(12345678|0{7,})$/.test(digitsOnly)) {
                    return false;
                }

                return true;
            },
        );

    const studentValidation = object({
        initials: string().required(label("site.errors.required")),
        insertion: string().nullable(),
        last_name: string().required(label("site.errors.required")),
        first_name: string().required(label("site.errors.required")),
        email: emailValidation,
        date_of_birth: dateOfBirthValidation,
        phone: phoneValidation.required(label("site.errors.required")),
    });

    const contactDetailsValidation = object({
        initials: string().required(label("site.errors.required")),
        insertion: string().nullable(),
        last_name: string().required(label("site.errors.required")),
        first_name: string().required(label("site.errors.required")),
        email: emailValidation,
        phone: phoneValidation.required(label("site.errors.required")),
    });

    const invoiceDetailsValidation = object({
        terms_of_service: boolean()
            .required(label("site.errors.terms_of_service"))
            .isTrue(),
        organisation: string().required(label("site.errors.required")),
        postal: string().required(label("site.errors.required")),
        house_number: string().required(label("site.errors.required")),
        street: string().required(label("site.errors.required")),
        city: string().required(label("site.errors.required")),
        reference: string().optional().nullable(),
        email: emailValidation,
        phone: phoneValidation.required(label("site.errors.required")),
    });

    const offerDetailsValidation = object({
        company: string().required(label("site.errors.required")),
        insertion: string().nullable(),
        name: string().required(label("site.errors.required")),
        email: emailValidation,
        phone: phoneValidation.required(label("site.errors.required")),
        quantity: string().required(label("site.errors.required")),
        message: string().required(label("site.errors.required")),
    });

    const contactFormValidation = object({
        first_name: string().required(label("site.errors.required")),
        insertion: string().nullable(),
        last_name: string().required(label("site.errors.required")),
        email: emailValidation,
        phone: phoneValidation.optional().nullable(),
        message: string().required(label("site.errors.required")),
    });

    enum EnrollmentStatus {
        step_1 = 1,
        step_2 = 2,
        step_3 = 3,
    }

    const init = (enrollmentDraft: EnrollmentDraftResponse) => {
        enrollment.value = enrollmentDraft;

        if (enrollment.value.students !== null) {
            students.value = enrollment.value.students;
        } else {
            const studentStore = sessionStorage.getItem(
                `students-${enrollment.value.uuid}`,
            );
            if (studentStore) {
                students.value = JSON.parse(studentStore);
            } else {
                //add empty
                students.value.push({
                    uuid: generateUuid(),
                    initials: "",
                    first_name: "",
                    last_name: "",
                    date_of_birth: null,
                    phone: "",
                    email: "",
                });
            }
        }

        if (enrollment.value.contact_details !== null) {
            contactDetails.value = enrollment.value.contact_details;
        } else {
            const contactDetailsStore = sessionStorage.getItem(
                `contactDetails-${enrollment.value.uuid}`,
            );
            if (contactDetailsStore) {
                contactDetails.value = JSON.parse(contactDetailsStore);
            }
        }

        if (enrollment.value.invoice_details !== null) {
            invoiceDetails.value = enrollment.value.invoice_details;
        } else {
            const invoiceDetailsStore = sessionStorage.getItem(
                `invoiceDetails-${enrollment.value.uuid}`,
            );
            if (invoiceDetailsStore) {
                invoiceDetails.value = JSON.parse(invoiceDetailsStore);
            } else {
                invoiceDetails.value = {
                    organisation: "",
                    phone: "",
                    email: "",
                    postal: "",
                    city: "",
                    house_number: "",
                    street: "",
                };
            }
        }

        uuid.value = enrollment.value.uuid;

        currentStep.value = EnrollmentStatus[enrollment.value.status];
        startOpened(enrollment.value.status);
        isLoading.value = false;
    };

    const startOpened = (step) => {
        if (step) {
            const status = EnrollmentStatus[step];
            if (status === EnrollmentStatus.step_1) openStep1.value = true;
            if (status === EnrollmentStatus.step_2) openStep2.value = true;
            if (status === EnrollmentStatus.step_3) openStep3.value = true;
        }
    };

    const studentCount = computed(() => {
        return students.value?.length ?? 1;
    });

    const addStudent = () => {
        return saveStep1(false).then(() => {
            const uuid = generateUuid();
            students.value.push({
                uuid: uuid,
                initials: "",
                first_name: "",
                last_name: "",
                date_of_birth: null,
                phone: "",
                email: "",
            });

            return uuid;
        });
    };

    const removeStudent = (index) => {
        const studentIndex = students.value.findIndex(
            (student) => student.uuid === index,
        );
        if (studentIndex !== -1) {
            students.value.splice(studentIndex, 1);
        }
        saveStep1(false);
    };

    const isValidStep1 = computed(() => {
        if (Object.keys(contactDetails.value).length !== 0) {
            try {
                contactDetailsValidation.validateSync(contactDetails.value);
            } catch {
                return false;
            }
            contactDetailsValidation.validateSync(contactDetails.value);
        }

        return students.value.every((student) => {
            try {
                studentValidation.validateSync(student);
                return true;
            } catch {
                return false;
            }
        });
    });

    const isValidStep2 = computed(() => {
        if (Object.keys(invoiceDetails.value).length !== 0) {
            try {
                invoiceDetailsValidation.validateSync(invoiceDetails.value);
            } catch {
                return false;
            }
            return true;
        }

        return false;
    });

    const extraQueryParameters = computed(() => {
        return `&prijs=${(enrollment.value.item.price.price_excl / 100).toFixed(2)}&cursist=${studentCount.value}`;
    });

    const totalPrice = computed(() => {
        return (
            students.value.length * enrollment.value.item?.price?.price_excl ??
            0
        );
    });

    const generateUuid = () => {
        return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
            (
                +c ^
                (crypto.getRandomValues(new Uint8Array(1))[0] &
                    (15 >> (+c / 4)))
            ).toString(16),
        );
    };

    const saveStep1 = async (nextStep): Promise<any> => {
        return new Promise((resolve, reject) => {
            isLoading.value = true;

            const payload = {
                uuid: uuid.value,
                students: students.value,
                contact_details: contactDetails.value,
                next_step: nextStep,
            };

            if (saveStepRequest.abort !== null) {
                saveStepRequest.abort.abort();
            }

            const abortController: AbortController = new AbortController();
            saveStepRequest.abort = abortController;

            try {
                const config: AxiosRequestConfig = {
                    signal: abortController.signal,
                };

                api()
                    .post(
                        "/site/enrollment/step-1/",
                        _.merge(payload, { _method: "POST" }),
                        config,
                    )
                    .then((response) => {
                        if (nextStep) {
                            currentStep.value = EnrollmentStatus.step_2;
                            openStep1.value = false;

                            moveToStep(2);
                        }

                        resolve(response.data);
                    })
                    .catch((response) => {
                        if (response.status === 422) {
                            reject(response.response.data.errors);
                        } else if (response.status === 500) {
                            toast.error(label("site.errors.500"));
                        }
                    })
                    .finally(() => {
                        isLoading.value = false;
                    });
            } catch (e) {
                reject(e);
                isLoading.value = false;
            }
        });
    };

    const saveStep2 = async (): Promise<any> => {
        return new Promise((resolve, reject) => {
            isLoading.value = true;
            const payload = {
                uuid: uuid.value,
                invoice_details: invoiceDetails.value,
            };

            if (saveStepRequest.abort !== null) {
                saveStepRequest.abort.abort();
            }

            const abortController: AbortController = new AbortController();
            saveStepRequest.abort = abortController;

            try {
                const config: AxiosRequestConfig = {
                    signal: abortController.signal,
                };

                api()
                    .post(
                        "/site/enrollment/step-2/",
                        _.merge(payload, { _method: "POST" }),
                        config,
                    )
                    .then((response) => {
                        currentStep.value = EnrollmentStatus.step_3;
                        openStep2.value = false;
                        openStep3.value = true;

                        moveToStep(3);

                        resolve(response.data);
                    })
                    .catch((response) => {
                        if (response.status === 422) {
                            reject(response.response.data.errors);
                        } else if (response.status === 500) {
                            toast.error(label("site.errors.500"));
                        }
                    })
                    .finally(() => {
                        isLoading.value = false;
                    });
            } catch (e) {
                isLoading.value = false;
                reject(e);
            }
        });
    };

    const saveStep3 = async (): Promise<any> => {
        return new Promise((resolve, reject) => {
            isLoading.value = true;
            const payload = {
                uuid: uuid.value,
            };

            if (saveStepRequest.abort !== null) {
                saveStepRequest.abort.abort();
            }

            const abortController: AbortController = new AbortController();
            saveStepRequest.abort = abortController;

            try {
                const config: AxiosRequestConfig = {
                    signal: abortController.signal,
                };

                api()
                    .post(
                        "/site/enrollment/step-3/",
                        _.merge(payload, { _method: "POST" }),
                        config,
                    )
                    .then((response) => {
                        //redirect to success
                        window.location.href = response.data.redirect;
                        resolve(response.data);
                    })
                    .catch((response) => {
                        if (response.status === 422) {
                            reject(response.response.data.errors);
                        } else if (response.status === 500) {
                            toast.error(label("site.errors.500"));
                        }
                    })
                    .finally(() => {
                        isLoading.value = false;
                    });
            } catch (e) {
                reject(e);
                isLoading.value = false;
            }
        });
    };

    const moveToStep = (step: number) => {
        if (step === EnrollmentStatus.step_1) {
            openStep1.value = true;
        } else if (step === EnrollmentStatus.step_2) {
            openStep2.value = true;
        } else if (step === EnrollmentStatus.step_3) {
            openStep3.value = true;
        }

        nextTick(() => {
            document
                .getElementById(`step_${step}`)
                ?.scrollIntoView({ behavior: "smooth" });
        });
    };

    const filterLaravelErrors = (laravelErrors, prefix) => {
        const filteredErrors = {};
        const searchPrefix = `${prefix}.`;
        let maxIndex = -1;

        for (const [key, messages] of Object.entries(laravelErrors)) {
            if (key.startsWith(searchPrefix)) {
                const allErrors = Array.isArray(messages)
                    ? messages.join(" - ")
                    : messages;
                const parts = key.replace(searchPrefix, "").split(".");

                if (parts.length >= 2 && !Number.isNaN(parts[0])) {
                    const index = parseInt(parts[0], 10);
                    const fieldName = parts.slice(1).join(".");
                    maxIndex = Math.max(maxIndex, index);
                    if (!filteredErrors[index]) {
                        filteredErrors[index] = {};
                    }

                    filteredErrors[index][fieldName] = allErrors;
                } else {
                    const fieldName = parts.join(".");
                    filteredErrors[fieldName] = allErrors;
                }
            }
        }

        if (Object.keys(filteredErrors).some((key) => !Number.isNaN(key))) {
            const result = [];
            for (let i = 0; i <= maxIndex; i++) {
                result[i] = filteredErrors[i] || {};
            }
            return result;
        }

        return filteredErrors;
    };

    watch(currentStep, () => {
        updateUrl();
    });

    watch(studentCount, () => {
        updateUrl();
    });

    const updateUrl = () => {
        const url =
            window.language === "NL"
                ? `/inschrijven/${enrollment.value.item?.product?.erp_id}/${enrollment.value.product_code}`
                : `/en/enroll/${enrollment.value.item?.product?.erp_id}/${enrollment.value.product_code}`;

        window.history.pushState({}, "", `${url}/?stap=${currentStep.value}${extraQueryParameters.value}`);
    }

    watch(
        invoiceDetails,
        () => {
            sessionStorage.setItem(
                `invoiceDetails-${enrollment.value.uuid}`,
                JSON.stringify(invoiceDetails.value),
            );
        },
        { deep: true },
    );

    watch(
        contactDetails,
        () => {
            sessionStorage.setItem(
                `contactDetails-${enrollment.value.uuid}`,
                JSON.stringify(contactDetails.value),
            );
        },
        { deep: true },
    );

    watch(
        students,
        () => {
            sessionStorage.setItem(
                `students-${enrollment.value.uuid}`,
                JSON.stringify(students.value),
            );
        },
        { deep: true },
    );

    return {
        isLoading,
        currentStep,
        students,
        uuid,
        enrollment,
        contactDetails,
        invoiceDetails,
        contactDetailsValidation,
        studentValidation,
        invoiceDetailsValidation,
        offerDetailsValidation,
        contactFormValidation,
        isValidStep1,
        isValidStep2,
        totalPrice,
        openStep1,
        openStep2,
        openStep3,
        init,
        studentCount,
        addStudent,
        removeStudent,
        saveStep1,
        saveStep2,
        saveStep3,
        moveToStep,
        startOpened,
        filterLaravelErrors,
        generateUuid,
        EnrollmentStatus,
    };
});
